import { useRef } from "react";

import {
  Button,
  ButtonWithPopOut,
  useButtonWithPopOut,
} from "@newsela/angelou";
import PropTypes from "prop-types";

import { withTrackEvent } from "@newsela/snitch";

import {
  rootCss,
  buttonWithPopOutCss,
  dropdownWrapperCss,
  scrollableContainerCss,
} from "./ActionsDropdown.style";
import ActionsDropdownAnchorItem from "./ActionsDropdownAnchorItem";
import ActionsDropdownButtonItem from "./ActionsDropdownButtonItem";

const ButtonWithPopOutWithTrackEvent = withTrackEvent(ButtonWithPopOut);

const ActionsDropdownPropTypes = {
  ariaProps: PropTypes.object.isRequired,
  buttonCss: PropTypes.object,
  buttonContentsCss: PropTypes.object,
  buttonIcon: PropTypes.node,
  buttonFlavor: PropTypes.string,
  buttonRef: PropTypes.object,
  buttonSize: PropTypes.string,
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    .isRequired,
  dataProps: PropTypes.object,
  disabled: PropTypes.bool,
  dropdownCss: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object),
  ]),
  eventProperties: PropTypes.object,
  handleOutsideClick: PropTypes.func,
  horizontalOffset: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
  horizontalPopOutPosition: PropTypes.oneOf(["LEFT", "RIGHT"]),
  id: PropTypes.string.isRequired,
  isArticlePrintDisabled: PropTypes.bool.isRequired,
  popOutCss: PropTypes.shape({
    root: PropTypes.object,
    popOutContents: PropTypes.object,
  }),
  renderDropdownItems: PropTypes.func.isRequired,
  subMenuIsActive: PropTypes.bool,
};

export const DROPDOWN_CLOSE_DELAY = 550;

const ActionsDropdown = ({
  ariaProps,
  buttonContentsCss,
  buttonCss,
  buttonIcon,
  buttonFlavor,
  buttonRef,
  buttonSize = Button.sizes.md,
  buttonText,
  dataProps,
  dropdownCss,
  disabled = false,
  eventProperties,
  handleOutsideClick,
  horizontalOffset,
  horizontalPopOutPosition,
  id,
  popOutCss,
  renderDropdownItems,
  subMenuIsActive,
  isArticlePrintDisabled = false,
}) => {
  const internalButtonRef = useRef();
  const ref = buttonRef || internalButtonRef;

  const {
    isPopOutVisible,
    setIsPopOutVisible,
    onClickOff,
    ...controllingProps
  } = useButtonWithPopOut({ buttonRef: ref });

  const closeDropdown = () => setIsPopOutVisible(false);

  const handleOutsidePopOutClick = (e) => {
    // Delaying this function call to account for
    // popout closing animation
    handleOutsideClick &&
      setTimeout(() => {
        handleOutsideClick();
      }, DROPDOWN_CLOSE_DELAY);
    onClickOff(e);
  };

  const renderPopOutContents = () => {
    const dropdownItems = renderDropdownItems(closeDropdown);

    if (subMenuIsActive) {
      // We're using `scrollableContainerCss` here because `dropdownWrapperCss` includes list styles
      // that are only needed on `<ul>`s. When a submenu is active, the `<ul>` is nested more deeply.
      // As such, we're adding the list styles to the `<ul>` in `SubMenu.js`
      return (
        <div css={[scrollableContainerCss, dropdownCss]}>{dropdownItems}</div>
      );
    }
    return (
      <ul
        role="list"
        css={[dropdownWrapperCss(isArticlePrintDisabled), dropdownCss]}
      >
        {dropdownItems}
      </ul>
    );
  };

  return (
    <ButtonWithPopOutWithTrackEvent
      disabled={disabled}
      __cssFor={{
        root: rootCss,
        PopOut: popOutCss,
        Button: { root: [buttonWithPopOutCss, buttonCss] },
        buttonContents: buttonContentsCss,
      }}
      ariaProps={ariaProps}
      buttonContents={buttonText}
      buttonIcon={buttonIcon}
      dataProps={dataProps}
      flavor={buttonFlavor || Button.flavors.secondary}
      horizontalOffset={horizontalOffset || "0"}
      horizontalPopOutPosition={horizontalPopOutPosition || "LEFT"}
      id={id}
      isPopOutVisible={isPopOutVisible}
      onClickOff={(e) => handleOutsidePopOutClick(e)}
      popOutContents={renderPopOutContents()}
      ref={ref}
      setIsPopOutVisible={setIsPopOutVisible}
      size={buttonSize}
      {...controllingProps}
      {...eventProperties}
    />
  );
};

ActionsDropdown.propTypes = ActionsDropdownPropTypes;

ActionsDropdown.AnchorItem = ActionsDropdownAnchorItem;
ActionsDropdown.ButtonItem = ActionsDropdownButtonItem;

export default ActionsDropdown;
